import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-firm-details',
  standalone: false,
  templateUrl: './firm-details.component.html',
  styleUrls: ['./firm-details.component.css'],
})
export class FirmDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('tabsBar') tabsBarRef!: ElementRef;
  @ViewChild('dateTabsScroll') dateTabsScrollRef!: ElementRef<HTMLElement>;
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;
  @ViewChildren('lawyerCardRef') lawyerCardRefs!: QueryList<ElementRef>;

  canScrollLeft = false;
  canScrollRight = false;

  // ── Deep-link: expand a specific lawyer on load ────────────────
  private _pendingExpandLawyerId:any;
  // Flag: view has initialised, safe to scroll
  private _viewInitDone = false;

  // ── Firm data ─────────────────────────────────────────────────
  firm: any = null;
  firmId: string = '';

  // ── Tab ───────────────────────────────────────────────────────
  activeTab: 'about' | 'lawyers' = 'lawyers';

  // ── Lawyers tab ───────────────────────────────────────────────
  firmLawyers: any[] = [];
  lawyersLoading = false;
  lawyersError = '';
  lawyersCached = false;

  // ── Inline slot expansion ─────────────────────────────────────
  expandedLawyerIndex: number | null = null;
  slotsLoading = false;
  slotsError = ''  ;
  slotData: any = null;
  dateTabs: any[] = [];
  filteredSlots: any[] = [];
  selectedDateKey = '';

  // ── Selected lawyer internal tabs (Overview | Available Slots) ──
  // Lives alongside expandedLawyerIndex/slotData above — same fetch
  // (getLawyerProfile) already returns both profile + availability,
  // so no extra API call is needed to power the Overview tab.
  lawyerInternalTab: 'overview' | 'slots' = 'overview';
  lawyerAboutExpanded = false;
  // Per-item expand state for the "Cases Handled" bullet list (keyed by
  // index, since each case entry can be read-more'd independently).
  lawyerCasesExpandedMap: { [index: number]: boolean } = {};

  // ── Login Modal (identical to bookslot.component) ─────────────
  showLoginModal = false;
  private _pendingDay: any = null;
  private _pendingSlot: any = null;

  loginForm!: FormGroup;
  registerForm!: FormGroup;
  submitted = false;
  hidePassword = false;
  useOTP = true;
  otpSent = false;
  isRegisterMode = false;
  loginType: 'email' | 'mobile' = 'email';
  registerType: 'email' | 'mobile' = 'email';
  otpDigits: string[] = ['', '', '', '', '', ''];
  resendTimer = 60;
  canResend = false;
  currentEmailOrMobile = '';
  registeredFirstName = '';
  registeredLastName = '';
  private timerInterval: any;

  // ── Register Confirmation Dialog (mirrors bookslot.component) ──
  showConfirmDialog: boolean = false;
  confirmEmail: string = '';
  confirmPhone: string = '';

  // True once the backend has told us the email/mobile is already
  // registered and we've switched from "create account" to "log in via
  // OTP" for this attempt. Drives which API verifyRegisterOTP() calls.
  isLoginRedirectFlow: boolean = false;

  // Email entered for this registration attempt, kept so we can offer it
  // as the OTP channel if the backend responds with redirect_to === 'email'/'both'.
  private pendingEmail: string = '';

  // Message shown in the register flow's "OTP sent to your ..." banner.
  // Set dynamically from the backend response (or the redirect_to-derived
  // message) instead of being hardcoded, since the actual delivery channel
  // can be email or mobile depending on what the API decided.
  otpSentMessage: string = '';

  // ── Forgot / Reset Password (in-modal) ───────────────────────
  modalView: 'login' | 'forgot' | 'reset' = 'login';
  forgotPasswordForm!: FormGroup;
  resetPasswordForm!: FormGroup;
  forgotSubmitted = false;
  resetSubmitted = false;
  hideResetPassword = false;
  hideConfirmPassword = false;
  resetMail: string = '';

  // ── General ──────────────────────────────────────────────────
  isLoading = false;
  isLoggedIn = false;
  mobileMenuOpen = false;
  currentYear = new Date().getFullYear();

  private _firmNeedsRefresh = false;
  private destroy$ = new Subject<void>();
  isExistingUser = false;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const token = localStorage.getItem('TOKEN');
    this.isLoggedIn = !!token;
    console.log('[FirmDetails] Initial login state:', this.isLoggedIn);

    this.buildLoginForm();
    this.buildRegisterForm();
    this.buildForgotPasswordForm();
    this.buildResetPasswordForm();
      let route = this.route;
  while (route.firstChild) {
    route = route.firstChild;
  }


    // Always read firm from navigation state regardless of login status
    this.firm = history.state?.firm ?? null;

    // Normalize raw API address field if firm came from navigation state
    if (this.firm && !this.firm.address && history.state?.firm?.rawAddress) {
      this.firm.address = history.state.firm.rawAddress;
    }
    // Default to lawyers tab when bio is absent (Overview tab is hidden)
    if (this.firm && !this.firm.bio) {
      this.activeTab = 'lawyers';
    }
    console.log('[FirmDetails] Firm data from navigation state:', this.firm);

    // Pre-populate firmLawyers from prefetched state (sent by search-results bookSlot)
    const stateLawyers: any[] = history.state?.firmLawyers ?? [];
    if (stateLawyers.length > 0) {
      this.firmLawyers = stateLawyers.map((l: any) => this.transformLawyer(l));
      this.lawyersCached = true;
      console.log('[FirmDetails] Pre-populated firmLawyers from state:', this.firmLawyers.length);
      // Silently refresh firm card data from API (navigation state may have
      // incomplete firm fields e.g. missing yearsOfExperience)
      this._firmNeedsRefresh = true;
    }

    route.paramMap.subscribe(params => {
    const idFromRoute = params.get('id') || params.get('firmId');

    this.firmId = idFromRoute || this.firm?.firmId || this.firm?.id || '';

    console.log('FINAL firmId:', this.firmId);

    if (!this.firm && this.firmId) {
      //this.loadFirmById(this.firmId)
    }
  });
    console.log('[FirmDetails] Firm ID from route or state:', this.firmId);
    // ── Deep-link query params from "Book New Appointment" on a firm-lawyer ──
     this.route.queryParamMap.subscribe((qp:any) =>{
       const tabParam     = qp.get('tab');
    const expandLawyer = qp.get('expandLawyerId');

    if (tabParam === 'lawyers' && expandLawyer) {
      this._pendingExpandLawyerId = expandLawyer;
    }

     if (!this.firm && this.firmId) {
      // Firm data needs to be fetched from API first; deep-link handled inside loadFirmById
      this.loadFirmById(this.firmId,this._pendingExpandLawyerId);
    } else {
      // Firm data already available from navigation state — switch tab immediately
      if (this._pendingExpandLawyerId) {
        this.switchTab('lawyers');
      }
      // If lawyers were pre-populated from state, refresh firm card from API
      // to fill in any missing fields (e.g. yearsOfExperience) without re-fetching lawyers
      if (this._firmNeedsRefresh && this.firmId) {
        this._firmNeedsRefresh = false;
        this.refreshFirmCardFromApi(this.firmId);
      }
    }


     });
   

    
  }

  ngAfterViewInit() {
    this._viewInitDone = true;
  }

  ngOnDestroy() {
    this.stopResendTimer();
    this.destroy$.next();
    this.destroy$.complete();
    // Restore body scroll in case the login modal was open (openLoginModal
    // sets overflow: hidden) when the user navigated away — otherwise the
    // next page (e.g. checkout) is left unscrollable.
    document.body.style.overflow = '';
  }

  // ── Form Setup (identical to bookslot.component) ──────────────
  buildLoginForm() {
    this.loginForm = this.fb.group({
      emailOrMobile: ['', [Validators.required, this.validateEmailOrMobile.bind(this)]],
      password:      [''],
      otp:           ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      rememberMe:    [false]
    });
    this.loginForm.get('password')?.clearValidators();
    this.loginForm.get('password')?.updateValueAndValidity();
  }

  buildRegisterForm() {
    this.registerForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName:  ['', [Validators.required]],
      email:     ['', [Validators.required, this.validateEmail.bind(this)]],
      mobile:    ['', [Validators.required, this.validateMobile.bind(this)]],
      otp:       ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  buildForgotPasswordForm() {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  buildResetPasswordForm() {
    this.resetPasswordForm = this.fb.group({
      token:           ['', Validators.required],
      password:        ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.checkPasswordsMatch.bind(this) });
  }

  private checkPasswordsMatch(group: FormGroup) {
    const pass = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return pass === confirm ? null : { notSame: true };
  }

  get rf() { return this.resetPasswordForm.controls; }

  get f() { return this.loginForm.controls; }

  validateEmailOrMobile(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const mobilePattern = /^[0-9]{10,15}$/;
    return emailPattern.test(value) || mobilePattern.test(value) ? null : { invalidEmailOrMobile: true };
  }

  detectLoginType(value: string): 'email' | 'mobile' {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ? 'email' : 'mobile';
  }

  /**
   * Validate email format (register form)
   */
  validateEmail(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (emailPattern.test(value.trim())) {
      return null;
    }
    return { invalidEmail: true };
  }

  /**
   * Validate mobile number (register form):
   * - exactly 10 digits
   * - must start with 6, 7, 8, or 9
   * Leading zeros are stripped before validation
   */
  validateMobile(control: FormControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) return null;

    const cleaned = String(value).replace(/^0+/, '');
    const mobilePattern = /^[6-9]\d{9}$/;

    if (mobilePattern.test(cleaned)) {
      return null;
    }
    return { invalidMobile: true };
  }

  /**
   * Strip leading zeros/non-digits from the register form's mobile input in real time
   */
  onMobileInput(event: any): void {
    let inputValue: string = event.target.value;
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.replace(/^0+/, '');
    event.target.value = inputValue;
    this.registerForm.get('mobile')?.setValue(inputValue, { emitEvent: true });
  }

  // ── Modal Lifecycle (identical to bookslot.component) ─────────
  openLoginModal(day: any, slot: any) {
    console.log('[FirmDetails] Opening login modal with day:', day, 'slot:', slot);
    this._pendingDay = day;
    this._pendingSlot = slot;
    this.showLoginModal = true;

    // Reset states
    this.submitted = false;
    this.otpSent = false;
    this.isRegisterMode = false;
    this.useOTP = true;
    this.modalView = 'login';
    this.forgotSubmitted = false;
    this.resetSubmitted = false;
    this.resetOtpInputs();
    this.loginForm.reset({ rememberMe: false });
    this.registerForm.reset();
    this.forgotPasswordForm.reset();
    this.resetPasswordForm.reset();
    this.setOtpValidators();
    this.showConfirmDialog = false;
    this.isLoginRedirectFlow = false;
    this.otpSentMessage = '';
    this.pendingEmail = '';
    this.isExistingUser = false;

    document.body.style.overflow = 'hidden';
    console.log('[FirmDetails] Login modal opened, pending data saved');
  }

  closeLoginModal() {
    console.log('[FirmDetails] Closing login modal');
    this.showLoginModal = false;
    this._pendingDay = null;
    this._pendingSlot = null;
    this.submitted = false;
    this.otpSent = false;
    this.isRegisterMode = false;
    this.modalView = 'login';
    this.forgotSubmitted = false;
    this.resetSubmitted = false;
    this.showConfirmDialog = false;
    this.isLoginRedirectFlow = false;
    this.otpSentMessage = '';
    this.pendingEmail = '';
    this.stopResendTimer();
    document.body.style.overflow = '';
  }

  closeModalOnBackdrop(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeLoginModal();
    }
  }

  // ── Slot click — mirrors bookslot.component checkout() exactly ─
  checkoutSlot(day: any, slot: any) {
    console.log('[FirmDetails] checkoutSlot called with day:', day, 'slot:', slot);
    console.log('[FirmDetails] Slot available:', slot.available);

    if (!slot.available) {
      console.log('[FirmDetails] Slot not available, returning');
      return;
    }

    const token = localStorage.getItem('TOKEN') || sessionStorage.getItem('TOKEN');
    console.log('[FirmDetails] Token exists:', !!token);

    if (!token) {
      console.log('[FirmDetails] User not logged in, opening login modal');
      this.openLoginModal(day, slot);
      return;
    }

    // Token exists — verify it is still valid (mirrors bookslot.component exactly)
    this.auth.get_digicoffer_profile(true).subscribe({
      next: (_user: any) => {
        this.proceedToCheckout(day, slot);
      },
      error: (err: any) => {
        if (err.status === 401) {
          this.auth.clearStorage();
          this.openLoginModal(day, slot);
        } else {
          // Network/server error — proceed anyway, checkout page handles profile separately
          this.proceedToCheckout(day, slot);
        }
      }
    });
  }

  proceedToCheckout(day?: any, slot?: any) {
    console.log('[FirmDetails] proceedToCheckout called');
    const d = day ?? this._pendingDay;
    const s = slot ?? this._pendingSlot;

    console.log('[FirmDetails] Day:', d);
    console.log('[FirmDetails] Slot:', s);

    if (!d || !s) {
      console.log('[FirmDetails] Missing day or slot, returning');
      return;
    }

    // Get the lawyer ID from the current expanded lawyer
    const currentLawyer = this.expandedLawyerIndex !== null ? this.firmLawyers[this.expandedLawyerIndex] : null;
    console.log('[FirmDetails] Current lawyer:', currentLawyer);

    const lawyerId = currentLawyer?.lawyer_id || currentLawyer?.id;
    console.log('[FirmDetails] Lawyer ID:', lawyerId);

    const amount = this.slotData?.profile?.consultation_fee?.amount;
    console.log('[FirmDetails] Consultation amount:', amount);

    const checkoutState = {
      date: d.date,
      slot: { slot_id: s.slot_id, slot_time: s.start_time },
      lawyer: {
        lawyer_id: lawyerId,
        amount: amount
      },
      source: 'firm-details',
      firmId: this.firmId
    };

    console.log('[FirmDetails] Checkout state:', checkoutState);
    sessionStorage.setItem('checkout', JSON.stringify(checkoutState));
    localStorage.setItem('checkoutLog', (s.available === true).toString());
    this.router.navigate(['/checkout'], { state: checkoutState });
  }

  // ── Login Actions (identical to bookslot.component) ───────────
  onLoginButtonClicked() {
    console.log('[FirmDetails] onLoginButtonClicked called');
    console.log('OTP:', this.otpDigits.join(''));
    console.log('Form OTP:', this.loginForm.get('otp')?.value);

    this.submitted = true;

    // if (!this.isLoginFormValid()) {
    //   console.log('[FirmDetails] Login form invalid');
    //   return;
    // }
    if (!this.isLoginFormValid()) {

      console.log("EMAIL VALUE:", this.loginForm.get('emailOrMobile')?.value);
      console.log("EMAIL ERRORS:", this.loginForm.get('emailOrMobile')?.errors);

      console.log("OTP VALUE:", this.loginForm.get('otp')?.value);
      console.log("OTP ERRORS:", this.loginForm.get('otp')?.errors);

      console.log(this.loginForm);

      return;
    }

    const { emailOrMobile, password, otp, rememberMe } = this.loginForm.value;
    this.loginType = this.detectLoginType(emailOrMobile);
    this.currentEmailOrMobile = emailOrMobile;

    console.log('[FirmDetails] Login type:', this.loginType);
    console.log('[FirmDetails] Email/Mobile:', emailOrMobile);

    const loginPassword = this.useOTP ? '' : (password || '');
    //const loginOTP = this.useOTP && this.otpSent ? (otp || '') : '';
    const loginOTP =
      this.useOTP && this.otpSent
        ? this.otpDigits.join('')
        : '';

    this.auth.login(emailOrMobile, loginPassword, this.loginType, 'login', loginOTP)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          console.log('[FirmDetails] Login response:', res);
          this.handleLoginResponse(res, rememberMe, password, emailOrMobile);
        },
        error: (error) => {
          console.error('[FirmDetails] Login error:', error);
          this.toastr.error('Login failed. Please try again.', 'Error!');
        }
      });
  }

  private isLoginFormValid(): boolean {
    if (this.useOTP && !this.otpSent) return !this.loginForm.get('emailOrMobile')?.invalid;
    if (this.useOTP && this.otpSent) return !(this.loginForm.get('emailOrMobile')?.invalid || this.loginForm.get('otp')?.invalid);
    return !this.loginForm.invalid;
  }

  private handleLoginResponse(res: HttpResponse<any>, rememberMe: boolean, password: string, emailOrMobile: string): void {
    const body = res.body;
    console.log('[FirmDetails] Handle login response body:', body);

    if (!body) {
      this.toastr.error('Invalid server response', 'Error!');
      return;
    }

    if (body.otp_sent) {
      console.log('[FirmDetails] OTP sent, waiting for verification');
      this.otpSent = true;
      this.submitted = false;
      const destination = this.loginType === 'email' ? 'email' : 'mobile number';
      this.toastr.success(body.msg || `OTP sent to your ${destination}`, 'Success!');
      this.startResendTimer();
      setTimeout(() => this.otpInputs?.first?.nativeElement.focus(), 100);
      return;
    }

    if (body.error) {
      console.log('[FirmDetails] Login error in response:', body.error);
      this.toastr.error(body.msg || 'Login failed', 'Error!');
      return;
    }

    console.log('[FirmDetails] Login successful, storing session data');
    this.storeSessionData(body, emailOrMobile);
    this.handleRememberMe(rememberMe, password);
    this.toastr.success('Login successful');
    this.isLoggedIn = true;

    const day = this._pendingDay;
    const slot = this._pendingSlot;
    console.log('[FirmDetails] Pending day from modal:', day);
    console.log('[FirmDetails] Pending slot from modal:', slot);

    this.closeLoginModal();
    if (day && slot) {
      console.log('[FirmDetails] Proceeding to checkout after login');
      setTimeout(() => this.proceedToCheckout(day, slot), 300);
    } else {
      console.log('[FirmDetails] No pending slot found after login');
    }
  }

  // ── Register Actions (mirrors bookslot.component) ──────────────
  onRegisterButtonClicked(): void {
    console.log('[FirmDetails] onRegisterButtonClicked called');
    this.trimRegisterFormFields();
    this.submitted = true;

    if (this.otpSent) {
      this.registerForm.patchValue({
        otp: this.otpDigits.join('')
      });
    }

    if (!this.otpSent) {

      if (
        this.registerForm.get('firstName')?.invalid ||
        this.registerForm.get('lastName')?.invalid ||
        this.registerForm.get('email')?.invalid ||
        this.registerForm.get('mobile')?.invalid
      ) {
        console.log('[FirmDetails] Register form invalid');
        return;
      }

      // Show confirmation dialog instead of calling the API directly
      this.openRegisterConfirmDialog();

    } else {

      if (this.registerForm.get('otp')?.invalid) {
        console.log('[FirmDetails] OTP invalid');
        return;
      }

      this.verifyRegisterOTP();
    }
  }

  /**
   * Open confirmation dialog with the current email and mobile (register flow)
   */
  openRegisterConfirmDialog(): void {
    const emailValue = this.registerForm.get('email')?.value?.trim().toLowerCase();
    const mobileValue = this.registerForm.get('mobile')?.value?.replace(/^0+/, '');
    this.confirmEmail = emailValue;
    this.confirmPhone = mobileValue;
    this.showConfirmDialog = true;
  }

  /**
   * User clicked "No, Edit" — close dialog, return to form
   */
  onRegisterConfirmCancel(): void {
    this.showConfirmDialog = false;
  }

  /**
   * User clicked "Yes, Continue" — close dialog and trigger the send-OTP API
   */
  onRegisterConfirmProceed(): void {
    this.showConfirmDialog = false;
    this.sendRegisterOTP();
  }

  private trimRegisterFormFields(): void {
    const emailControl = this.registerForm.get('email');
    if (emailControl) {
      emailControl.setValue(emailControl.value?.trim().toLowerCase() ?? '');
    }
    const mobileControl = this.registerForm.get('mobile');
    if (mobileControl) {
      mobileControl.setValue(mobileControl.value?.replace(/^0+/, '').trim() ?? '');
    }
    const firstNameControl = this.registerForm.get('firstName');
    if (firstNameControl) {
      firstNameControl.setValue(firstNameControl.value?.trim() ?? '');
    }
    const lastNameControl = this.registerForm.get('lastName');
    if (lastNameControl) {
      lastNameControl.setValue(lastNameControl.value?.trim() ?? '');
    }
  }

  private sendRegisterOTP(): void {
    const firstName = this.registerForm.get('firstName')?.value?.trim();
    const lastName = this.registerForm.get('lastName')?.value?.trim();
    const email = this.registerForm.get('email')?.value?.trim().toLowerCase();
    const mobile = this.registerForm.get('mobile')?.value?.replace(/^0+/, '');

    // Store for later use
    this.registeredFirstName = firstName;
    this.registeredLastName = lastName;
    this.currentEmailOrMobile = mobile;
    this.pendingEmail = email;
    this.registerType = 'mobile';
    this.isLoginRedirectFlow = false;

    // Call authService to send OTP via register API using mobile, also
    // forwarding email so the backend can detect an email-based conflict
    this.auth.sendOtpForRegistration(mobile, firstName, 'mobile', 'register', lastName, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.handleRegisterOtpSent(response);
        },
        error: (error) => {
          this.handleSendRegisterOtpError(error);
        }
      });
  }

  /**
   * Handle OTP sent successfully (register flow)
   */
  private handleRegisterOtpSent(response: HttpResponse<any>): void {
    const isSuccess = response.body?.otp_sent ||
                     (response.body?.error === false) ||
                     (response.status === 200 || response.status === 201);

    if (isSuccess) {
      const msg = response.body?.msg || 'OTP sent to your mobile number';

      // The backend may deliver the OTP via email even though registration
      // was submitted with logintype 'mobile' (e.g. it prioritizes a
      // verified email when both are provided). Detect the actual channel
      // from the response message so verify/resend target the right
      // identifier, and the banner/toast reflect what really happened.
      if (msg.toLowerCase().includes('email') && this.pendingEmail) {
        this.registerType = 'email';
        this.currentEmailOrMobile = this.pendingEmail;
      }

      this.otpSent = true;
      this.submitted = false;
      this.canResend = false;
      this.resendTimer = 60;
      this.otpSentMessage = msg;

      this.toastr.success(msg, 'Success!');
      this.startResendTimer();
      setTimeout(() => this.otpInputs?.first?.nativeElement.focus(), 100);
    } else {
      this.toastr.error('Failed to send OTP. Please try again.', 'Error!');
    }
  }

  /**
   * Handle send-OTP error (register flow), including the "already
   * registered" redirect_to case where we switch to logging the user in
   * via OTP instead.
   */
  private handleSendRegisterOtpError(error: any): void {
    console.error('[FirmDetails] Registration OTP error:', error);

    const body = error?.error;
    const errorMessage = body?.message || body?.msg || 'Failed to send OTP. Please try again.';
    const redirectTo: 'email' | 'mobile' | 'both' | undefined = body?.redirect_to;

    // Already-in-use detection: prefer the structured redirect_to flag from
    // the API; fall back to the previous message-sniffing for backends/errors
    // that don't send redirect_to yet.
    const isAlreadyInUse =
      !!redirectTo ||
      error.status === 409 ||
      errorMessage.toLowerCase().includes('already registered') ||
      errorMessage.toLowerCase().includes('already exists') ||
      errorMessage.toLowerCase().includes('already in use') ||
      errorMessage.toLowerCase().includes('already');

    if (!isAlreadyInUse) {
      this.toastr.error(errorMessage, 'Error!');
      return;
    }

    // Pick which channel to send the login OTP to, and the matching message.
    // redirect_to === 'email' or 'both' -> prioritize email.
    // redirect_to === 'mobile'          -> use mobile.
    // no redirect_to (legacy fallback)  -> keep prior behavior (mobile).
    let identifierType: 'email' | 'mobile';
    let identifierValue: string;
    let alreadyInUseMsg: string;

    if (redirectTo === 'email') {
      identifierType = 'email';
      identifierValue = this.pendingEmail;
      alreadyInUseMsg = 'Email is already registered. Please log in using the OTP sent to your inbox.';
    } else if (redirectTo === 'mobile') {
      identifierType = 'mobile';
      identifierValue = this.currentEmailOrMobile;
      alreadyInUseMsg = 'Phone number is already registered. Please log in using the OTP sent via SMS.';
    } else if (redirectTo === 'both') {
      identifierType = 'email';
      identifierValue = this.pendingEmail;
      alreadyInUseMsg = 'Account already exists. Please login via OTP sent to your inbox.';
    } else {
      identifierType = 'mobile';
      identifierValue = this.currentEmailOrMobile;
      alreadyInUseMsg = 'Account already exists. OTP sent successfully.';
    }

    this.registerType = identifierType;
    this.currentEmailOrMobile = identifierValue;
    this.isLoginRedirectFlow = true;
    this.isExistingUser = true;

    // Call LOGIN API to send OTP to the resolved channel
    this.auth.login(identifierValue, '', identifierType, 'login')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body?.otp_sent || res.body?.error === false) {
            this.otpSent = true;
            this.submitted = false;
            this.canResend = false;
            this.resendTimer = 60;

            setTimeout(() => {
              this.otpInputs?.first?.nativeElement.focus();
            }, 100);

            this.registerForm.get('otp')?.setValidators([
              Validators.required,
              Validators.pattern(/^\d{6}$/)
            ]);
            this.registerForm.get('otp')?.updateValueAndValidity();

            this.startResendTimer();
            this.otpSentMessage = alreadyInUseMsg;
            this.toastr.success(alreadyInUseMsg);
          } else {
            this.toastr.error(
              res.body?.msg || 'Failed to send OTP',
              'Error!'
            );
          }
        },
        error: () => {
          this.toastr.error('Failed to send OTP. Please try again.', 'Error!');
        }
      });
  }

  private verifyRegisterOTP(): void {

    const otp = this.otpDigits.join('');

    if (!otp || otp.length !== 6) {
      this.registerForm.get('otp')?.setErrors({ pattern: true });
      return;
    }

    // Existing account (redirect_to case) — verify via the same login API
    // used for OTP login, not the register-verify API.
    if (this.isLoginRedirectFlow || this.isExistingUser) {

      this.auth.login(
        this.currentEmailOrMobile,
        '',
        this.registerType,
        'login',
        otp
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: HttpResponse<any>) => {
            this.handleLoginResponse(
              res,
              false,
              '',
              this.currentEmailOrMobile
            );
          },
          error: () => {
            this.toastr.error('OTP verification failed.', 'Error!');
          }
        });

      return;
    }

    // New User
    this.auth.verifyOtpForRegistration(
      this.currentEmailOrMobile,
      otp,
      this.registerType,
      'login'
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('[FirmDetails] Verify OTP response:', response);

          const body = response.body;

          if (!body) {
            this.toastr.error('Invalid server response', 'Error!');
            return;
          }

          if (body.error) {
            this.toastr.error(body.msg || 'OTP verification failed.', 'Error!');
            return;
          }

          this.storeSessionData(body, this.currentEmailOrMobile);
          this.isLoggedIn = true;

          this.toastr.success(body.msg || 'Registration successful!', 'Success!');

          // Proceed
          const day = this._pendingDay;
          const slot = this._pendingSlot;
          console.log('[FirmDetails] Pending data after registration:', { day, slot });

          this.closeLoginModal();
          if (day && slot) {
            setTimeout(() => this.proceedToCheckout(day, slot), 300);
          }
        },
        error: (error) => {
          console.error('[FirmDetails] Verify OTP error:', error);
          const errorMessage = error.error?.message || error.error?.msg || 'OTP verification failed.';
          this.toastr.error(errorMessage, 'Error!');
        }
      });
  }

  // ── Shared Helpers (identical to bookslot.component) ──────────
  private storeSessionData(body: any, emailOrMobile: string): void {
    console.log('[FirmDetails] Storing session data');
    const data = body.data;
    const token = data.access_token;

    sessionStorage.setItem('TOKEN', token);
    localStorage.setItem('TOKEN', token);

    const resolvedType = this.isRegisterMode ? this.registerType : this.loginType;

    if (data) {
      localStorage.setItem('PK', data.pk);
      localStorage.setItem('REFRESH_TOKEN', data.refresh_token);
      localStorage.setItem('first_name', data.first_name || this.registeredFirstName);
      localStorage.setItem('last_name', data.last_name || this.registeredLastName);
      localStorage.setItem('email', data.email || (resolvedType === 'email' ? emailOrMobile : ''));
      localStorage.setItem('mobile', data.mobile || (resolvedType === 'mobile' ? emailOrMobile : ''));
      localStorage.setItem('full_name', `${data.first_name || this.registeredFirstName} ${data.last_name || this.registeredLastName}`.trim());
    }

    localStorage.setItem('login_type', resolvedType);
  }

  private handleRememberMe(rememberMe: boolean, password: string): void {
    if (!this.isRegisterMode) {
       if (rememberMe && !this.useOTP) {
        localStorage.setItem('remembered_email_or_mobile', this.currentEmailOrMobile);
        localStorage.setItem('remembered_password', password);
        localStorage.setItem('remembered_checked', 'true');
      } else {
        localStorage.removeItem('remembered_email_or_mobile');
        localStorage.removeItem('remembered_password');
        localStorage.removeItem('remembered_checked');
      }
    }
  }

  toggleLoginMethod() {
    this.useOTP = !this.useOTP;
    this.otpSent = false;
    this.submitted = false;
    this.resetOtpInputs();
    this.loginForm.patchValue({ otp: '' });

    if (this.useOTP) {
      this.setOtpValidators();
    } else {
      this.setPasswordValidators();
    }
  }

  setOtpValidators() {
    this.loginForm.get('password')?.clearValidators();
    this.loginForm.get('emailOrMobile')?.setValidators([Validators.required, this.validateEmailOrMobile.bind(this)]);
    this.loginForm.get('otp')?.setValidators([Validators.required, Validators.pattern(/^\d{6}$/)]);
    this.loginForm.get('password')?.updateValueAndValidity();
    this.loginForm.get('emailOrMobile')?.updateValueAndValidity();
    this.loginForm.get('otp')?.updateValueAndValidity();
  }

  setPasswordValidators() {
    this.loginForm.get('password')?.setValidators([Validators.required]);
    this.loginForm.get('emailOrMobile')?.setValidators([Validators.required, Validators.email]);
    this.loginForm.get('otp')?.clearValidators();
    this.loginForm.get('password')?.updateValueAndValidity();
    this.loginForm.get('emailOrMobile')?.updateValueAndValidity();
    this.loginForm.get('otp')?.updateValueAndValidity();
  }

  cancelOTP() {
    console.log('[FirmDetails] Cancelling OTP flow');
    this.otpSent = false;
    this.submitted = false;
    this.resetOtpInputs();
    if (this.isRegisterMode) {
      this.registerForm.get('otp')?.reset();
      this.registerForm.patchValue({ otp: '' });
      this.isLoginRedirectFlow = false;
      this.isExistingUser = false;
      this.otpSentMessage = '';
    } else {
      this.loginForm.get('otp')?.reset();
      this.loginForm.patchValue({ otp: '' });
    }
    this.stopResendTimer();
  }

  // ── OTP Inputs (identical to bookslot.component) ──────────────
  resetOtpInputs() { this.otpDigits = ['', '', '', '', '', '']; }

  onOtpDigitInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9]/g, '');
    if (value.length > 1) value = value.slice(-1);
    input.value = '';
    this.otpDigits[index] = value;
    input.value = value;

    // Update respective form
    const form = this.isRegisterMode ? this.registerForm : this.loginForm;
    form.patchValue({ otp: this.otpDigits.join('') }, { emitEvent: false });

    if (value.length === 1 && index < 5) {
      setTimeout(() => {
        const next = this.otpInputs.toArray()[index + 1]?.nativeElement;
        if (next) { next.focus(); next.select(); }
      }, 0);
    }
  }

  // Backspace deletes one digit *and* steps the cursor back, so clearing the
  // code box by box never leaves the cursor on the box just emptied.
  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const prev = input.previousElementSibling as HTMLInputElement | null;
    const next = input.nextElementSibling as HTMLInputElement | null;

    if (event.key === 'Backspace') {
      event.preventDefault();

      if (this.otpDigits[index]) {
        this.otpDigits[index] = '';
        input.value = '';
      } else if (prev) {
        this.otpDigits[index - 1] = '';
        prev.value = '';
      }

      const form = this.isRegisterMode ? this.registerForm : this.loginForm;
      form.patchValue({ otp: this.otpDigits.join('') }, { emitEvent: false });
      prev?.focus();
    } else if (event.key === 'ArrowLeft' && prev) {
      event.preventDefault();
      prev.focus();
    } else if (event.key === 'ArrowRight' && next) {
      event.preventDefault();
      next.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const digits = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    for (let i = 0; i < digits.length; i++) this.otpDigits[i] = digits[i];

    const form = this.isRegisterMode ? this.registerForm : this.loginForm;
    form.patchValue({ otp: this.otpDigits.join('') }, { emitEvent: false });

    setTimeout(() => {
      const inputs = this.otpInputs.toArray();
      inputs[Math.min(digits.length - 1, 5)]?.nativeElement.focus();
    }, 0);
  }

  startResendTimer() {
    this.resendTimer = 60;
    this.canResend = false;
    this.timerInterval = setInterval(() => {
      this.resendTimer--;
      if (this.resendTimer <= 0) { this.canResend = true; clearInterval(this.timerInterval); }
    }, 1000);
  }

  stopResendTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval);
    this.resendTimer = 60;
    this.canResend = false;
  }

  resendOTP() {
    console.log('[FirmDetails] Resending OTP');
    if (!this.canResend) return;
    this.submitted = false;
    this.resetOtpInputs();

    if (this.isRegisterMode) {
        // Resend for Register
        const emailOrMobile = this.currentEmailOrMobile;
        const type = this.registerType;
        this.auth.login(emailOrMobile, '', type, 'login')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: HttpResponse<any>) => {
              if (res.body?.otp_sent) {
                const destination = type === 'email' ? 'email' : 'mobile number';
                this.toastr.success(res.body.msg || `OTP resent to your ${destination}`, 'Success!');
                this.startResendTimer();
              } else {
                this.toastr.error('Failed to resend OTP', 'Error!');
              }
            },
            error: () => this.toastr.error('Failed to resend OTP', 'Error!')
          });
    } else {
        // Resend for Login
        const emailOrMobile = this.currentEmailOrMobile;
        const loginType = this.loginType;
        this.auth.login(emailOrMobile, '', loginType, 'login')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: HttpResponse<any>) => {
              if (res.body?.otp_sent) {
                this.toastr.success(res.body.msg || 'OTP resent', 'Success!');
                this.startResendTimer();
              } else {
                this.toastr.error('Failed to resend OTP', 'Error!');
              }
            },
            error: () => this.toastr.error('Failed to resend OTP', 'Error!')
          });
    }
  }

  // ── Load firm by ID (direct URL) ─────────────────────────────
  loadFirmById(id: string, lawyer_id:any) {
    this.firmId = id;   // ← guarantee firmId is set before loadFirmLawyers() fires
    this.isLoading = true;
  
    this.auth.getLawyerProfile(lawyer_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          if (res?.data) {
            const d = res.data;
            const hasSlots = d.availability?.slots?.length > 0;
            const avail =  hasSlots ? 'Available' : 'Unavailable';
            this.firm = {
              id:lawyer_id,
              firmId: id,
              name: d.profile?.name || 'Unknown Firm',
              avatar: d.profile?.profile_pic_url || '',
              location: [d.profile?.address?.city_town, d.profile?.address?.state]
                .filter(Boolean).join(', '),
              specializations: d.profile?.practice_areas || [],
              bio: d.profile?.bio || '',
              memberCount: d.memberCount || null,
              availability: avail,
              availabilityStyle: avail.toLowerCase() === 'unavailable'
                ? { background: '#e5e7eb', color: '#6b7280' }
                : { background: '#d1fae5', color: '#065f46' },
              address: d.profile?.address || null,
              yearsOfExperience:
                d.profile?.experience?.years ??
                d.profile?.yearsOfExperience ??
                d.yearsOfExperience ??
                null,
              isFirm: true,
            };
          }
          // Default to lawyers tab when bio is absent (Overview tab is hidden)
          if (!this.firm.bio) {
            this.activeTab = 'lawyers';
          }
          // ── Deep-link: now that firm is loaded, switch to lawyers tab ──
          if (this._pendingExpandLawyerId) {
            this.switchTab('lawyers');
          }
        },
        error: () => { this.isLoading = false; }
      });
  }

  // ── Book Appointment clicked on firm hero card ────────────────
  // Immediately calls the search API with firmId (same as loadFirmLawyers)
  // then switches to the lawyers tab so results are ready when the tab renders.
  bookAppointmentForFirm() {
    if (this.lawyersCached) {
      // Already fetched — just switch to the lawyers tab
      this.switchTab('lawyers');
      return;
    }

    // Pre-fetch lawyers via search API with firmId, then switch tab
    const firmId = this.firmId ||
      this.route.snapshot.paramMap.get('id') ||
      this.route.snapshot.paramMap.get('firmId') || '';

    this.lawyersLoading = true;
    this.lawyersError = '';

    this.auth.lawyerPageSearch({ firmId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.lawyersLoading = false;

          // Always update firm card from API res.firm — overrides navigation state
          // which may have incomplete fields (e.g. yearsOfExperience missing)
          if (res?.firm) {
            this.firm = this.transformFirmFromSearch(res.firm);
            // Keep lawyers tab active when bio is absent
            if (!this.firm.bio && this.activeTab === 'about') {
              this.activeTab = 'lawyers';
            }
            console.log('[FirmDetails] bookAppointmentForFirm: firm card updated from API res.firm:', this.firm);
          }

          const raw: any[] = res?.lawyers || [];
          this.firmLawyers = raw
            .filter(l => !l.isFirm)
            .map(l => this.transformLawyer(l));
          this.lawyersCached = true;
          this.activeTab = 'lawyers';
          setTimeout(() => {
            this.tabsBarRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 50);
        },
        error: (err: any) => {
          this.lawyersLoading = false;
          this.lawyersError = err?.error?.msg || 'Failed to load lawyers. Please try again.';
          this.activeTab = 'lawyers';
        }
      });

    // Switch to lawyers tab immediately so the loading spinner is visible
    this.activeTab = 'lawyers';
    setTimeout(() => {
      this.tabsBarRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  // ── Tab switching ─────────────────────────────────────────────
  switchTab(tab: 'about' | 'lawyers') {
    this.activeTab = tab;
    if (tab === 'lawyers' && !this.lawyersCached) {
      this.loadFirmLawyers();
    }
    if (tab === 'about') {
      this.collapseSlots();
    }
    // Only scroll to tabs bar for manual tab clicks (not deep-link expand, which handles its own scroll)
    if (!this._pendingExpandLawyerId) {
      setTimeout(() => {
        this.tabsBarRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  // ── Fetch lawyers for this firm ───────────────────────────────
  loadFirmLawyers() {
    const firmId = this.firmId || this.route.snapshot.paramMap.get('id') || this.route.snapshot.paramMap.get('firmId') || '';
    console.log('[FirmDetails] loadFirmLawyers firmId:', firmId);
    this.lawyersLoading = true;
    this.lawyersError = '';
    this.auth.lawyerPageSearch({ firmId: firmId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.lawyersLoading = false;

          // Always update firm card from API res.firm — overrides navigation state
          // which may have incomplete fields (e.g. yearsOfExperience missing)
          if (res?.firm) {
            this.firm = this.transformFirmFromSearch(res.firm);
            // Keep lawyers tab active when bio is absent
            if (!this.firm.bio && this.activeTab === 'about') {
              this.activeTab = 'lawyers';
            }
            console.log('[FirmDetails] loadFirmLawyers: firm card updated from API res.firm:', this.firm);
          }

          const raw: any[] = res?.lawyers || [];
          this.firmLawyers = raw
            .filter(l => !l.isFirm)
            .map(l => this.transformLawyer(l));
          this.lawyersCached = true;

          // ── Deep-link: expand and scroll to the requested lawyer ──
          if (this._pendingExpandLawyerId) {
            const pendingId = this._pendingExpandLawyerId;
            this._pendingExpandLawyerId = null;     // clear BEFORE expand so switchTab scroll-guard works normally afterwards
            this.expandLawyerFromRoute(pendingId);
          }
        },
        error: (err: any) => {
          this.lawyersLoading = false;
          this.lawyersError = err?.error?.msg || 'Failed to load lawyers. Please try again.';
        }
      });
  }

  // ── Deep-link: find a lawyer by id, expand slots, scroll to card ──
  private expandLawyerFromRoute(lawyerId: string): void {
    const index = this.firmLawyers.findIndex(
      l => String(l.lawyer_id) === String(lawyerId) || String(l.id) === String(lawyerId)
    );

    if (index === -1) {
      console.warn('[FirmDetails] Deep-link: lawyer not found for id:', lawyerId);
      return;
    }

    const lawyer = this.firmLawyers[index];
    if (lawyer.availability === 'Unavailable') {
      console.warn('[FirmDetails] Deep-link: lawyer is unavailable:', lawyer.name);
      return;
    }

    console.log('[FirmDetails] Deep-link: expanding lawyer at index', index, lawyer.name);

    // Set expanded index so *ngIf renders the slot-expansion-wrapper immediately
    this.expandedLawyerIndex = index;
    this.slotsLoading = true;
    this.slotsError = '';
    this.slotData = null;
    this.dateTabs = [];
    this.filteredSlots = [];
    this.selectedDateKey = '';
    // Deep-links land users straight on the slot picker (existing behaviour) —
    // Overview data still loads in the background from the same response.
    this.lawyerInternalTab = 'slots';
    this.lawyerAboutExpanded = false;
    this.lawyerCasesExpandedMap = {};

    // Force Angular to render the expanded card before we try to scroll
    this.cdr.detectChanges();

    // First scroll: bring the card header into view right away
    this.scrollToLawyerCard(index);

    // Fetch slot data (same as toggleSlotExpansion but without collapsing if already expanded)
    this.auth.getLawyerProfile(lawyer.lawyer_id || lawyer.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.slotsLoading = false;
          if (!res?.data) {
            this.slotsError = 'Could not load slots. Please try again.';
            return;
          }
          this.slotData = res.data;
          const slots: any[] = res.data?.availability?.slots || [];

          if (slots.length === 0) {
            this.slotsError = 'No available slots found for this lawyer.';
            return;
          }

          this.buildDateTabs(slots.map(day => this.categoriseSlots(day)));

          const todayKey = this.getDateKey(new Date());
          const defaultDate =
            slots.find((d: any) => d.date === todayKey)?.date || slots[0]?.date;

          if (defaultDate) {
            this.selectedDateKey = defaultDate;
            this.filteredSlots = slots
              .filter((d: any) => d.date === defaultDate)
              .map(day => this.categoriseSlots(day));
          }

          // Second scroll: now that slots are rendered the card is taller — scroll again
          // so the slot picker is visible in the viewport
          this.cdr.detectChanges();
          setTimeout(() => {
            this.scrollToLawyerCard(index);
            this.updateScrollArrows();
          }, 80);
        },
        error: (err: any) => {
          console.error('[FirmDetails] Error loading slots:', err);
          this.slotsLoading = false;
          this.slotsError = err?.error?.msg || 'Failed to load slots. Please try again.';
        }
      });
  }

  // ── Shared scroll helper ──────────────────────────────────────
  private scrollToLawyerCard(index: number): void {
    const cards = this.lawyerCardRefs?.toArray();
    const cardEl = cards?.[index]?.nativeElement as HTMLElement | undefined;
    if (cardEl) {
      cardEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ── Toggle slot expansion for a lawyer card (manual click) ────
  toggleSlotExpansion(index: number, lawyer: any) {
    console.log('[FirmDetails] Toggle slot expansion for lawyer:', lawyer.name);
    if (lawyer.availability === 'Unavailable') {
      console.log('[FirmDetails] Lawyer unavailable, returning');
      return;
    }

    if (this.expandedLawyerIndex === index) {
      console.log('[FirmDetails] Collapsing slots');
      this.collapseSlots();
      return;
    }

    console.log('[FirmDetails] Expanding slots for lawyer:', lawyer.name);
    this.expandedLawyerIndex = index;
    this.slotsLoading = true;
    this.slotsError = '';
    this.slotData = null;
    this.dateTabs = [];
    this.filteredSlots = [];
    this.selectedDateKey = '';
    // Overview is always the default internal tab when a lawyer is expanded
    this.lawyerInternalTab = 'overview';
    this.lawyerAboutExpanded = false;
    this.lawyerCasesExpandedMap = {};

    this.auth.getLawyerProfile(lawyer.lawyer_id || lawyer.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          console.log('[FirmDetails] Slot data loaded for lawyer:', lawyer.name);
          this.slotsLoading = false;
          if (!res?.data) {
            this.slotsError = 'Could not load slots. Please try again.';
            return;
          }
          this.slotData = res.data;
          const slots: any[] = res.data?.availability?.slots || [];

          if (slots.length === 0) {
            this.slotsError = 'No available slots found for this lawyer.';
            return;
          }

          this.buildDateTabs(slots.map(day => this.categoriseSlots(day)));

          const todayKey = this.getDateKey(new Date());
          const defaultDate =
            slots.find((d: any) => d.date === todayKey)?.date || slots[0]?.date;

          if (defaultDate) {
            this.selectedDateKey = defaultDate;
            this.filteredSlots = slots
              .filter((d: any) => d.date === defaultDate)
              .map(day => this.categoriseSlots(day));
          }

          this.cdr.detectChanges();
          setTimeout(() => this.updateScrollArrows(), 80);
        },
        error: (err: any) => {
          console.error('[FirmDetails] Error loading slots:', err);
          this.slotsLoading = false;
          this.slotsError = err?.error?.msg || 'Failed to load slots. Please try again.';
        }
      });
  }

  private collapseSlots() {
    this.expandedLawyerIndex = null;
    this.slotData = null;
    this.dateTabs = [];
    this.filteredSlots = [];
    this.slotsError = '';
    this.selectedDateKey = '';
    this.lawyerInternalTab = 'overview';
    this.lawyerAboutExpanded = false;
    this.lawyerCasesExpandedMap = {};
  }

  // ── Selected-lawyer internal tabs (Overview | Available Slots) ──
  // Pure UI state switch — no API call, no data reset. slotData (profile +
  // availability) was already fetched by toggleSlotExpansion/expandLawyerFromRoute
  // and stays intact while the user moves between tabs, so any slot already
  // selected is preserved when they come back to "Available Slots".
  switchLawyerTab(tab: 'overview' | 'slots') {
    this.lawyerInternalTab = tab;
  }

  // ── Overview tab helpers ────────────────────────────────────────
  private static readonly LO_TRUNCATE_LIMIT = 220;

  isLongText(text: string | null | undefined): boolean {
    return !!text && text.length > FirmDetailsComponent.LO_TRUNCATE_LIMIT;
  }

  getTruncatedText(text: string | null | undefined, expanded: boolean): string {
    if (!text) return '';
    if (expanded || text.length <= FirmDetailsComponent.LO_TRUNCATE_LIMIT) return text;
    const cut = text.slice(0, FirmDetailsComponent.LO_TRUNCATE_LIMIT);
    // Trim to the last full word so we don't cut mid-word
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim() + '…';
  }

  // Normalizes profile.practice_at (shape not fixed by the backend — could be
  // an array of strings or objects) into { title, subtitle } rows for display.
  // Returns [] if nothing usable is present so the section can hide gracefully.
  getPracticeAt(profile: any): { title: string; subtitle: string }[] {
    const raw = profile?.practice_at;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw
      .map((p: any) => {
        if (typeof p === 'string') return { title: p, subtitle: '' };
        if (p && typeof p === 'object') {
          const title = p.name || p.type || p.center || p.title || p.court_name || p.label || '';
          const subtitle = p.subtitle
            || [p.location, p.city, p.city_town, p.state, p.place]
                .filter(Boolean)
                .join(' · ');
          return { title, subtitle };
        }
        return { title: '', subtitle: '' };
      })
      .filter((row: any) => row.title);
  }

  // Normalizes profile.cases_handled (string, or array of strings/objects)
  // into { title, description } entries so each case renders as its own
  // bullet instead of one run-on paragraph. Mirrors the normalisation used
  // for the lawyer's own Overview tab (bookslot's casesHandledItems).
  // Returns [] if nothing usable is present so the section can hide gracefully.
  getLawyerCasesItems(profile: any): { title: string; description: string }[] {
    const raw = profile?.cases_handled;
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : [raw];

    return list
      .map((c: any) => {
        if (c && typeof c === 'object') {
          return {
            title: c.title || c.case_type || c.name || c.heading || '',
            description: c.description || c.summary || c.text || ''
          };
        }
        const str = String(c || '').trim();
        // Split "Title: description" / "Title - description" style strings
        // into a bold heading + body; plain strings fall back to body only.
        const match = str.match(/^([^:\-–]{2,60})[:\-–]\s*(.+)$/);
        if (match) {
          return { title: match[1].trim(), description: match[2].trim() };
        }
        return { title: '', description: str };
      })
      .filter((item: any) => item.description);
  }

  toggleLawyerCaseExpanded(index: number): void {
    this.lawyerCasesExpandedMap[index] = !this.lawyerCasesExpandedMap[index];
  }

  // True once Overview has nothing to show for any of its three sections —
  // drives the graceful empty state instead of leaving blank space.
  hasNoOverviewData(profile: any): boolean {
    return !profile?.bio
      && this.getPracticeAt(profile).length === 0
      && this.getLawyerCasesItems(profile).length === 0;
  }

  selectDateTab(date: string) {
    this.selectedDateKey = date;
    const rawSlots: any[] = this.slotData?.availability?.slots || [];
    this.filteredSlots = rawSlots
      .filter((d: any) => d.date === date)
      .map(day => this.categoriseSlots(day));
    this.scrollActiveTabIntoView();
  }

  buildDateTabs(slots: any[]) {
    const today = new Date();
    const todayKey = this.getDateKey(today);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowKey = this.getDateKey(tomorrow);

    this.dateTabs = slots.map((d: any) => {
      const count =
        this.getAvailable(d.morning) +
        this.getAvailable(d.afternoon) +
        this.getAvailable(d.evening) +
        this.getAvailable(d.night);

      let label: string;
      if (d.date === todayKey) {
        label = 'Today';
      } else if (d.date === tomorrowKey) {
        label = 'Tomorrow';
      } else {
        const dateObj = new Date(d.date + 'T00:00:00');
        const dayName = dateObj.toLocaleString('en-US', { weekday: 'short' });
        const month   = dateObj.toLocaleString('en-US', { month: 'short' });
        const day     = dateObj.getDate();
        label = `${dayName} ${month} ${day}`;
      }

      return { label, date: d.date, available_slots: count };
    });
  }

  /**
   * Re-buckets all slots for a day into morning / afternoon / evening / night
   * regardless of how the API returned them.
   *   Morning   06:00–11:59
   *   Afternoon 12:00–16:59
   *   Evening   17:00–20:59
   *   Night     21:00–05:59
   */
  categoriseSlots(day: any): any {
    const all: any[] = [
      ...(day.morning   || []),
      ...(day.afternoon || []),
      ...(day.evening   || []),
      ...(day.night     || []),
    ];
    const morning: any[] = [], afternoon: any[] = [], evening: any[] = [], night: any[] = [];

    for (const slot of all) {
      const match = (slot.start_time || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (!match) { morning.push(slot); continue; }

      let hour = parseInt(match[1], 10);
      const min = parseInt(match[2], 10);
      const meridiem = match[3].toUpperCase();
      if (meridiem === 'PM' && hour !== 12) hour += 12;
      if (meridiem === 'AM' && hour === 12) hour = 0;
      const totalMins = hour * 60 + min;

      if      (totalMins >= 6 * 60  && totalMins < 12 * 60) morning.push(slot);
      else if (totalMins >= 12 * 60 && totalMins < 17 * 60) afternoon.push(slot);
      else if (totalMins >= 17 * 60 && totalMins < 21 * 60) evening.push(slot);
      else                                                    night.push(slot);
    }
    return { ...day, morning, afternoon, evening, night };
  }

  // ── Date-tab scroll helpers ───────────────────────────────────
  scrollDateTabs(direction: -1 | 1) {
    const el = this.dateTabsScrollRef?.nativeElement;
    if (el) el.scrollBy({ left: direction * 160, behavior: 'smooth' });
  }

  onDateTabsScroll() { this.updateScrollArrows(); }

  updateScrollArrows() {
    const el = this.dateTabsScrollRef?.nativeElement;
    if (!el) return;
    this.canScrollLeft  = el.scrollLeft > 0;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  private scrollActiveTabIntoView() {
    setTimeout(() => {
      const el = this.dateTabsScrollRef?.nativeElement;
      if (!el) return;
      const active = el.querySelector('.date.active') as HTMLElement;
      if (active) {
        const elLeft = el.getBoundingClientRect().left;
        const elRight = elLeft + el.clientWidth;
        const tabLeft = active.getBoundingClientRect().left;
        const tabRight = active.getBoundingClientRect().right;
        if (tabLeft < elLeft || tabRight > elRight) {
          active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
      this.updateScrollArrows();
    }, 50);
  }

  getAvailable(slots: any[]): number {
    return slots?.filter((s: any) => s.available).length || 0;
  }

  private getDateKey(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  // ── Silently refresh firm card data from API (used when lawyers came from nav state) ──
  private refreshFirmCardFromApi(firmId: string): void {
    this.auth.lawyerPageSearch({ firmId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.firm) {
            this.firm = this.transformFirmFromSearch(res.firm);
            // Keep lawyers tab active when bio is absent
            if (!this.firm.bio && this.activeTab === 'about') {
              this.activeTab = 'lawyers';
            }
            console.log('[FirmDetails] refreshFirmCardFromApi: firm card refreshed:', this.firm);
            this.cdr.detectChanges();
          }
        },
        error: () => { /* silent fail — firm card stays with nav state data */ }
      });
  }

  // ── Transform raw firm object from lawyerPageSearch API → display shape ──
  transformFirmFromSearch(f: any): any {
    const availability = f.availability || 'Unavailable';
    return {
      id: f.id,
      firmId: f.id,
      lawyer_id: f.lawyer_id,
      name: f.name || 'Unknown Firm',
      avatar: f.profilePicUrl || '',
      specializations: f.specializations || [],
      subSpecializations: f.subSpecializations || [],
      bio: f.bio || null,
      yearsOfExperience: f.yearsOfExperience || null,
      address: f.address || null,
      location: f.address
        ? [f.address.city, f.address.state].filter(Boolean).join(', ')
        : '',
      languages: (f.language || [])
        .map((lang: string) => lang.charAt(0).toUpperCase() + lang.slice(1))
        .join(', '),
      memberCount: f.memberCount || null,
      consultation_fee: f.consultation_fee || null,
      availability,
      availabilityStyle:
        availability.toLowerCase() === 'unavailable'
          ? { background: '#e5e7eb', color: '#6b7280' }
          : { background: '#d1fae5', color: '#065f46' },
      isFirm: true,
      isFirmMember: f.isFirmMember || false,
      specialization_counts: f.specialization_counts || null,
    };
  }

  // ── Transform raw API lawyer → display shape ──────────────────
  transformLawyer(l: any) {
    const availability = l.availability || 'Available';
    return {
      id: l.id,
      lawyer_id: l.lawyer_id,
      name: l.name || 'Unknown Lawyer',
      avatar: l.profilePicUrl || '',
      specialty: this.formatSpecialty(l.specializations),
      experience: l.yearsOfExperience || null,
      location: l.address
        ? [l.address.city, l.address.state].filter(Boolean).join(', ')
        : '',
      languages: (l.language || [])
        .map((lang: string) => lang.charAt(0).toUpperCase() + lang.slice(1))
        .join(', '),
      availability,
      availabilityStyle:
        availability.toLowerCase() === 'unavailable'
          ? { background: '#e5e7eb', color: '#6b7280' }
          : { background: '#d1fae5', color: '#065f46' },
    };
  }

  getFormattedAddress(address: any): string {
    if (!address) return '';
    return [address.city || address.city_town, address.state, address.postcode]
      .filter(Boolean)
      .join(', ');
  }

  getFullAddress(address: any): string {
    if (!address) return '';
    return [address.street, address.city || address.city_town, address.state, address.postcode, address.country]
      .filter(Boolean)
      .join(', ');
  }

  getSpecText(specializations: string[]): string {
    if (!specializations?.length) return '';
    return specializations.map(s => this.formatSpec(s)).join(', ');
  }

  goBack() { window.history.back(); }

  formatSpec(spec: unknown): string {
    if (typeof spec !== 'string') return '';
    return spec.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatSpecialty(specializations: string[]): string {
    if (!specializations?.length) return 'General Practice';
    return specializations.map(s => this.formatSpec(s)).join(', ');
  }

  getInitial(name: string): string {
    return name?.trim().charAt(0).toUpperCase() || 'L';
  }

  restricttextSpace(event: any) {
    let v: string = event.target.value;
    v = v.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
    event.target.value = v;
  }

  // ── Forgot / Reset Password (in-modal flow) ───────────────────

  goToForgotPassword() {
    this.modalView = 'forgot';
    this.forgotSubmitted = false;
    this.forgotPasswordForm.reset();
  }

  onForgotPasswordSubmit() {
    this.forgotSubmitted = true;
    if (this.forgotPasswordForm.invalid) return;
    const email = this.forgotPasswordForm.value.email;
    this.auth.forgot_password(email).subscribe({
      next: (res: HttpResponse<any>) => {
        if (!res.body?.error) {
          localStorage.setItem('reemail', email);
          localStorage.setItem('coffer_id', res.body.data.coffer_id);
          this.resetMail = email;
          this.resetPasswordForm.reset();
          this.resetSubmitted = false;
          this.hideResetPassword = false;
          this.hideConfirmPassword = false;
          this.modalView = 'reset';
        } else {
          this.toastr.error(res.body.msg || 'Something went wrong', 'Error!');
        }
      },
      error: (err: any) => {
        if (err.status === 404) {
          this.toastr.error(err.error?.msg, 'Error');
        } else {
          this.toastr.error('An unexpected error occurred. Please try again later.', 'Error!');
        }
      }
    });
  }

  onResetPasswordSubmit() {
    this.resetSubmitted = true;
    if (this.resetPasswordForm.invalid) return;
    const coffer_id = localStorage.getItem('coffer_id') ?? '';
    const { token, password } = this.resetPasswordForm.value;
    this.auth.submit_password(coffer_id, password, token).subscribe({
      next: (res: HttpResponse<any>) => {
        if (!res.body?.error) {
          this.toastr.success('Password reset successful. Please log in.');
          const rememberedEmail = localStorage.getItem('reemail') || '';
          this.loginForm.reset({ rememberMe: false });
          this.loginForm.patchValue({ emailOrMobile: rememberedEmail });
          this.useOTP = false;
          this.otpSent = false;
          this.isRegisterMode = false;
          this.resetSubmitted = false;
          this.modalView = 'login';
        } else {
          this.toastr.error(res.body.msg, 'Error!');
        }
      },
      error: (err: any) => {
        this.toastr.error(err.error?.msg || 'Something went wrong.', 'Error!');
      }
    });
  }

  resendResetToken() {
    const coffer_id = localStorage.getItem('coffer_id') ?? '';
    this.auth.resend_password_token(coffer_id).subscribe((res: HttpResponse<any>) => {
      if (res.body && res.body.error === false) {
        this.toastr.success(res.body.msg);
      }
    });
  }
}